<script lang="ts">
  // IMPORTANT: Svelte 5에서는 수동 레이아웃 래핑이 필요합니다
  // Inertia.js의 자동 레이아웃 할당이 Svelte 5와 호환되지 않음
  import { router } from '@inertiajs/svelte';

  interface Props {
    path?: string;
  }

  let { path = '' }: Props = $props();

  function goToDashboard() {
    router.visit('/admin/dashboard');
  }

  function goBack() {
    window.history.back();
  }
</script>

  <div class="min-h-screen flex items-center justify-center px-4 py-12">
    <!-- Glassmorphism Container -->
    <div class="max-w-2xl w-full">
      <!-- Glass Card -->
      <div
        class="relative overflow-hidden rounded-3xl bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl border border-gray-200/50 dark:border-gray-700/50 shadow-2xl p-8 md:p-12"
      >
        <!-- Gradient Background Blur -->
        <div class="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-purple-500/5 pointer-events-none"></div>

        <!-- Content -->
        <div class="relative z-10">
          <!-- 404 Number -->
          <div class="text-center mb-8">
            <div class="inline-flex items-center justify-center">
              <span
                class="text-9xl md:text-[12rem] font-bold bg-gradient-to-br from-primary via-purple-500 to-pink-500 bg-clip-text text-transparent leading-none"
              >
                404
              </span>
            </div>
          </div>

          <!-- Title -->
          <h1 class="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white text-center mb-4">
            페이지를 찾을 수 없습니다
          </h1>

          <!-- Description -->
          <p class="text-lg text-gray-600 dark:text-gray-400 text-center mb-8 max-w-md mx-auto">
            요청하신 페이지가 존재하지 않거나 이동되었을 수 있습니다.
            {#if path}
              <span class="block mt-2 text-sm font-mono text-gray-500 dark:text-gray-500">
                {path}
              </span>
            {/if}
          </p>

          <!-- Navigation Buttons -->
          <div class="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <!-- Primary: Dashboard -->
            <button
              onclick={goToDashboard}
              class="group relative px-8 py-3 rounded-xl bg-gradient-to-r from-primary to-purple-600 text-white font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 cursor-pointer"
            >
              <span class="flex items-center gap-2">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/>
                </svg>
                대시보드로 이동
              </span>
            </button>

            <!-- Secondary: Go Back -->
            <button
              onclick={goBack}
              class="px-8 py-3 rounded-xl bg-white/50 dark:bg-gray-800/50 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-semibold backdrop-blur-sm hover:bg-white/70 dark:hover:bg-gray-800/70 transition-all duration-300 hover:scale-105 cursor-pointer"
            >
              <span class="flex items-center gap-2">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
                </svg>
                이전 페이지
              </span>
            </button>
          </div>

          <!-- Quick Links -->
          <div class="mt-12 pt-8 border-t border-gray-200/50 dark:border-gray-700/50">
            <p class="text-sm text-gray-500 dark:text-gray-400 text-center mb-4">
              자주 찾는 페이지
            </p>
            <div class="flex flex-wrap gap-3 justify-center">
              <a
                href="/admin"
                class="px-4 py-2 rounded-lg text-sm text-gray-600 dark:text-gray-400 hover:text-primary dark:hover:text-primary hover:bg-gray-100/50 dark:hover:bg-gray-800/50 transition-colors cursor-pointer"
              >
                📊 대시보드
              </a>
              <a
                href="/admin/threads_posts"
                class="px-4 py-2 rounded-lg text-sm text-gray-600 dark:text-gray-400 hover:text-primary dark:hover:text-primary hover:bg-gray-100/50 dark:hover:bg-gray-800/50 transition-colors cursor-pointer"
              >
                🧵 Threads 게시글
              </a>
              <a
                href="/admin/users"
                class="px-4 py-2 rounded-lg text-sm text-gray-600 dark:text-gray-400 hover:text-primary dark:hover:text-primary hover:bg-gray-100/50 dark:hover:bg-gray-800/50 transition-colors cursor-pointer"
              >
                👥 사용자
              </a>
              <a
                href="/settings"
                class="px-4 py-2 rounded-lg text-sm text-gray-600 dark:text-gray-400 hover:text-primary dark:hover:text-primary hover:bg-gray-100/50 dark:hover:bg-gray-800/50 transition-colors cursor-pointer"
              >
                ⚙️ 설정
              </a>
            </div>
          </div>
        </div>

        <!-- Decorative Elements -->
        <div class="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-primary/20 to-purple-500/20 rounded-full blur-3xl opacity-30 pointer-events-none"></div>
        <div class="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-pink-500/20 to-primary/20 rounded-full blur-3xl opacity-30 pointer-events-none"></div>
      </div>

      <!-- Footer Hint -->
      <p class="text-center mt-6 text-sm text-gray-500 dark:text-gray-400">
        문제가 계속되면 관리자에게 문의하세요
      </p>
    </div>
  </div>
